import posthog from 'posthog-js';
import { getHoursMinutesFormat, getTimezoneFormattedDate } from '../utils/dateUtils';
import { Environments } from '../constants/environments';

interface IBIServiceGlobalData {
	projectId: string;
	tz: string;
}

interface IBIBasicEventData {
	projectId: string;
	projectLocalDate: string;
	projectLocalTime: string;
}

/**
 * The product-analytics transport. Every `*_BI` helper in the apps funnels through `logEvent`,
 * so this class is the only place that knows which vendor receives them.
 *
 * PostHog replaced Amplitude here: the Amplitude project stopped receiving anything in November
 * 2020 — years before the current `BIEventNames` instrumentation was written — so none of these
 * events had ever arrived anywhere. PostHog is already initialised for both apps, already
 * receives pageviews and replays, and sits far inside its free tier.
 *
 * This class does NOT init PostHog and does NOT own identity. `initPostHog` /
 * `initPostHogByUserData` (shared/utils/posthog.utils.ts) do both, and they run from the
 * bootstrap AFTER this is constructed. Calling `identify` here as the Amplitude version did
 * would set a second, different distinct id (`user_<id>` vs the `_id` posthog.utils uses) and
 * split every person in two.
 */
class BIService<EventNames> {
	private config: any;

	private globalData: IBIServiceGlobalData | undefined;

	private readonly appVersion: string;

	private readonly shouldSendEvent: boolean;

	// `prod-jp` was missing, so Japan silently dropped every BI event even when the transport
	// was healthy — the same class of invisible failure that hid Amplitude's death.
	private environmentsToSend: string[] = [
		Environments.prod,
		Environments.prodJp,
		Environments.prodSg,
		Environments.qa,
	];

	constructor(config, versionService) {
		this.config = config;
		this.appVersion = versionService.getVersion() || '';
		this.shouldSendEvent = this.environmentsToSend.includes(config.environment);
	}

	/**
	 * Enriches the person PostHog has already identified. Deliberately not `identify` — see the
	 * class comment. `setPersonProperties` attaches to whoever the current distinct id is, so
	 * this stays correct whether or not `initPostHogByUserData` has run yet.
	 */
	public initUserIdentify(userData) {
		if (!this.shouldSendEvent) {
			return;
		}
		posthog?.setPersonProperties({
			username: userData.username,
			roleType: userData.roleType,
		});
	}

	/**
	 * Also declares the active project as a PostHog *group*. Trusstor is multi-tenant by
	 * `projectId`, and group analytics ("which projects are engaged", "which contractors use
	 * the safety features") is not answerable from a person property alone.
	 */
	public setGlobalData(data: IBIServiceGlobalData) {
		this.globalData = data;
		if (!this.shouldSendEvent) {
			return;
		}
		posthog?.group('project', data.projectId);
	}

	public logEvent(eventName: EventNames, eventData?: any) {
		try {
			// No project context yet (pre-login, or before the working project loads): an event
			// without its project fields was never sent anyway — it used to throw into the catch
			// below — so skip it quietly instead of spraying TypeErrors in the console.
			if (!this.shouldSendEvent || !this.globalData) {
				return;
			}
			const fullData: IBIBasicEventData = {
				...eventData,
				projectId: this.globalData.projectId,
				projectLocalDate: getTimezoneFormattedDate(this.globalData.tz, new Date()),
				projectLocalTime: getHoursMinutesFormat(new Date(), this.globalData.tz),
				appVersion: this.appVersion,
			};
			posthog?.capture(String(eventName), fullData);
		} catch (error) {
			console.error(error);
		}
	}
}

export { BIService };
